import { useSuspenseQuery } from "@tanstack/react-query";
import {
  ColumnSizingState,
  ExpandedState,
  getCoreRowModel,
  getExpandedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  Table,
  TableOptions,
  Updater,
  useReactTable,
  VisibilityState,
} from "@tanstack/react-table";
import { queries, useAuthConfig } from "@theprelude/core";
import React, { useCallback, useRef } from "react";
import { createJSONStorage, persist } from "zustand/middleware";
import { shallow } from "zustand/shallow";
import { createWithEqualityFn as create } from "zustand/traditional";
import { tableFuzzyFilter } from "@/lib/fuzzy-filters";

type TOptions<TData> = Omit<
  TableOptions<TData>,
  | "getCoreRowModel"
  | "getExpandedRowModel"
  | "getFilteredRowModel"
  | "getIsRowExpanded"
  | "getPaginationRowModel"
  | "getSortedRowModel"
  | "onColumnVisibilityChange"
  | "onExpandedChange"
  | "onGlobalFilterChange"
  | "onColumnSizingChange"
  | "paginateExpandedRows"
> & {
  name: string;
};

export function useTable<TData>(options: TOptions<TData>): Table<TData> {
  const authConfig = useAuthConfig();
  const accountQuery = useSuspenseQuery(queries.iam.getAccount(authConfig));
  const { state, initialState, meta, columns: cols, ...rest } = options;

  const [expanded, setExpanded] = React.useState<ExpandedState>({});
  const [columnSizing, setColumnSizing] = useTableColumnSizing(options.name);
  const [globalFilter, setGlobalFilter] = React.useState(
    initialState?.globalFilter ?? "",
  );

  const [columnVisibility, setColumnVisibility] = useTableVisibility(
    options.name,
    initialState?.columnVisibility,
  );

  const columns = React.useMemo(
    () =>
      cols.filter((c) =>
        c.meta?.authorize ? c.meta.authorize(accountQuery.data) : true,
      ),
    [cols, accountQuery.data],
  );

  const handleColumnSizingChange = useCallback(
    (updater: Updater<ColumnSizingState>) => {
      const newSizing =
        typeof updater === "function" ? updater(columnSizing) : updater;
      setColumnSizing(newSizing);
    },
    [columnSizing, setColumnSizing],
  );

  return useReactTable({
    globalFilterFn: tableFuzzyFilter,
    columns,
    state: {
      expanded,
      globalFilter,
      columnVisibility,
      columnSizing,
      ...state,
    },
    meta: { ...meta, name: options.name },
    initialState,
    ...rest,
    onExpandedChange: setExpanded,
    onGlobalFilterChange: setGlobalFilter,
    onColumnVisibilityChange: (updater) => {
      if (typeof updater === "function") {
        setColumnVisibility(updater(columnVisibility));
      } else {
        setColumnVisibility(updater);
      }
    },
    onColumnSizingChange: handleColumnSizingChange,
    getIsRowExpanded: (row) =>
      expanded === true ? expanded : (expanded[row.id] ?? false),
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: meta?.getExpandableContent
      ? getExpandedRowModel()
      : undefined,
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: initialState?.pagination
      ? getPaginationRowModel()
      : undefined,
    getSortedRowModel: getSortedRowModel(),
    columnResizeMode: "onChange",
    enableColumnResizing: true,
  });
}

export function getGridTemplateColumns(table: Table<any>) {
  return table
    .getVisibleFlatColumns()
    .map((column) => {
      const columnSizing = table.getState().columnSizing;
      const hasColumnSizing = Boolean(columnSizing[column.id]);
      const canResize = column.getCanResize();

      const columnSize = column.getSize();

      if (!canResize) {
        return `${columnSize}px`;
      }

      if (!hasColumnSizing) {
        return `minmax(${columnSize}px, 1fr)`;
      }

      const size = columnSizing[column.id];

      if (size && size < 100) {
        return "100px";
      }

      return `${size}px`;
    })
    .join(" ");
}

interface TableColumnVisibilityStore {
  tables: Record<string, VisibilityState>;
  setTableVisibility: (
    tableName: string,
    columnVisibility: VisibilityState,
  ) => void;
  resetTableVisibility: (tableName: string) => void;
}

const useTableVisibilityStore = create<TableColumnVisibilityStore>()(
  persist(
    (set) => ({
      tables: {},
      setTableVisibility: (
        tableName: string,
        columnVisibility: VisibilityState,
      ) => {
        set((state) => {
          const tables = {
            ...state.tables,
            [tableName]: { ...columnVisibility },
          };
          return { tables };
        });
      },
      resetTableVisibility: (tableName: string) => {
        set((state) => {
          const { [tableName]: _, ...remainingTables } = state.tables;
          return { tables: remainingTables };
        });
      },
    }),
    {
      name: `prelude:table-column-visibility`,
      storage: createJSONStorage(() => localStorage),
    },
  ),
);

export function resetTableVisibility(tableName: string) {
  useTableVisibilityStore.getState().resetTableVisibility(tableName);
}

interface TableColumnSizingStore {
  tables: Record<string, ColumnSizingState>;
  setTableSizing: (tableName: string, columnSizing: ColumnSizingState) => void;
}

const useTableColumnSizingStore = create<TableColumnSizingStore>()(
  persist(
    (set) => ({
      tables: {},
      setTableSizing: (tableName: string, columnSizing: ColumnSizingState) => {
        set((state) => {
          const tables = {
            ...state.tables,
            [tableName]: { ...columnSizing },
          };
          return { tables };
        });
      },
    }),
    {
      name: `prelude:table-column-sizing`,
      storage: createJSONStorage(() => localStorage),
    },
  ),
);

function useTableColumnSizing(tableName: string) {
  const columnSizing = useTableColumnSizingStore((state) => {
    return state.tables[tableName] ?? {};
  }, shallow);

  const setColumnSizing = useTableColumnSizingStore((state) => {
    return state.setTableSizing;
  });

  const setColumnSizingFn = React.useCallback(
    (columnSizing: ColumnSizingState) => {
      setColumnSizing(tableName, columnSizing);
    },
    [tableName, setColumnSizing],
  );

  return [columnSizing, setColumnSizingFn] as const;
}

function useTableVisibility(tableName: string, initialState?: VisibilityState) {
  const columnVisibility = useTableVisibilityStore((state) => {
    return state.tables[tableName] ?? initialState ?? {};
  }, shallow);

  const setColumnVisibility = useTableVisibilityStore(
    (state) => state.setTableVisibility,
  );

  const setColumnVisibilityFn = React.useCallback(
    (columnVisibility: VisibilityState) => {
      setColumnVisibility(tableName, columnVisibility);
    },
    [tableName, setColumnVisibility],
  );

  return [columnVisibility, setColumnVisibilityFn] as const;
}

/**
 * Custom hook to measure and manage header widths in table components
 */
export function useHeaderWidths<TData>(table: Table<TData>) {
  const headerRefs = useRef<Record<string, HTMLTableHeaderCellElement | null>>(
    {},
  );

  const getAllHeaderWidths = useCallback(() => {
    const headerWidths: Record<string, number> = {};

    table.getHeaderGroups().forEach((headerGroup) => {
      headerGroup.headers.forEach((header) => {
        const headerElement = headerRefs.current[header.id];
        if (headerElement) {
          headerWidths[header.id] = headerElement.getBoundingClientRect().width;
        }
      });
    });

    return headerWidths;
  }, [table]);

  const applyAllHeaderWidths = useCallback(() => {
    const gridTemplateColumns = getGridTemplateColumns(table);

    if (gridTemplateColumns.includes("minmax")) {
      const widths = getAllHeaderWidths();
      table.setColumnSizing(widths);
    }
  }, [getAllHeaderWidths, table]);

  const registerHeaderRef = useCallback(
    (id: string, element: HTMLTableHeaderCellElement | null) => {
      headerRefs.current[id] = element;
    },
    [],
  );

  return {
    headerRefs,
    getAllHeaderWidths,
    applyAllHeaderWidths,
    registerHeaderRef,
  };
}
